import React from 'react'
import Link from 'next/link'
import {
	Title,
	Card,
	Text,
	Group,
	Stack,
	Badge,
	Box,
	Flex,
	Pagination,
	Center,
	Spoiler,
	Grid,
} from '@mantine/core'
import NextImage from 'next/image'

import * as Types from '../declarations'
import {
	getUploadThumbPath,
	getTrekRoute,
	formatDifficulty,
} from '../lib/display'
import HTMLContent from './HTMLContent'
import CountryBadge from './CountryBadge'

// Define months array to ensure consistent formatting
const MONTHS = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
]

interface ExpeditionCardProps {
	trek: Types.App.ExpeditionListTrip
}

const MonthBadges: React.FC<{ bestMonths: (string | number)[] }> = ({
	bestMonths,
}) => {
	return (
		<Group gap={4}>
			{MONTHS.map((month, index) => {
				const monthNum = index + 1
				const isGoodMonth = bestMonths.some((m) => +m === monthNum)
				return (
					<Badge
						key={month}
						variant={isGoodMonth ? 'filled' : 'outline'}
						size="sm"
						styles={{
							root: {
								opacity: isGoodMonth ? 1 : 0.6,
								fontWeight: isGoodMonth ? 600 : 400,
							},
						}}
					>
						{month}
					</Badge>
				)
			})}
		</Group>
	)
}

const ExpeditionCard: React.FC<ExpeditionCardProps> = ({ trek }) => {
	const trekUrl = getTrekRoute(
		trek.trekOrClimb,
		trek.continent || 'no-continent',
		trek.countries,
		trek.slug,
	)

	const difficulty = trek?.climbDifficulty
		? formatDifficulty(trek.climbDifficulty, 'shortDescription')
		: undefined

	return (
		<Card shadow="sm" withBorder mb="md" p="md" w="100%">
			<Flex
				direction={{ base: 'column', sm: 'row' }}
				gap={{ base: 'md', sm: 24 }}
			>
				{/* Image Section */}
				<Box
					style={{
						width: '100%',
						maxWidth: '300px',
						flexShrink: 0,
						position: 'relative',
						overflow: 'hidden',
					}}
					w={{ base: '100%', sm: '300px' }}
				>
					{trek.firstUploadId && (
						<NextImage
							src={getUploadThumbPath(trek.firstUploadId, 'medium')}
							alt={trek.name}
							width={600}
							height={400}
							style={{
								width: '100%',
								height: 'auto',
								display: 'block',
								borderRadius: '8px',
							}}
							sizes="(max-width: 768px) 100vw, 300px"
						/>
					)}
				</Box>

				{/* Content Section */}
				<Box
					style={{
						flex: 1,
						minWidth: 0, // Prevent flex item from overflowing
					}}
				>
					<Stack gap="xs" style={{ height: '100%' }}>
						<Group align="center" gap="xs" wrap="nowrap">
							<Link href={trekUrl}>
								<Title order={2} size="h3" style={{ display: 'inline' }}>
									{trek.name}
								</Title>
							</Link>
							{trek.countries.map((country, index) => (
								<CountryBadge key={index} isoCode={country} />
							))}
							<Box style={{ flex: 1 }} />
							{trek.lowestCost && (
								<Badge size="lg" variant="filled">
									From ${(+trek.lowestCost).toLocaleString()}
								</Badge>
							)}
						</Group>

						{trek?.description && (
							<Spoiler
								maxHeight={100}
								showLabel="Show more"
								hideLabel="Show less"
							>
								<HTMLContent html={trek.description} />
							</Spoiler>
						)}

						<Grid gap="md">
							{difficulty && (
								<Grid.Col span={6}>
									<Text size="sm">
										<b>Difficulty:</b> {difficulty}
									</Text>
								</Grid.Col>
							)}
							{trek.height && (
								<Grid.Col span={6}>
									<Text size="sm">
										<b>Height:</b> {trek.height.toLocaleString()}m
									</Text>
								</Grid.Col>
							)}
							{trek.totalDaysLength && trek.totalDaysLength[0] && (
								<Grid.Col span={6}>
									<Text size="sm">
										<b>Duration:</b> {trek.totalDaysLength[0]} days
									</Text>
								</Grid.Col>
							)}
						</Grid>

						{trek.bestMonths && trek.bestMonths.length > 0 && (
							<Box>
								<Text size="sm" mb={4}>
									<b>Best time to climb:</b>
								</Text>
								<MonthBadges bestMonths={trek.bestMonths} />
							</Box>
						)}

						<Group justify="right" mt="auto">
							<Link href={trekUrl}>
								<Text size="sm" style={{ textDecoration: 'underline' }}>
									View Details →
								</Text>
							</Link>
						</Group>
					</Stack>
				</Box>
			</Flex>
		</Card>
	)
}

interface TripListProps {
	treks: Types.App.ExpeditionListTrip[]
	version: Types.VERSION
}

export const TripList: React.FC<TripListProps> = ({ treks, version }) => {
	const [activePage, setActivePage] = React.useState(1)
	const ITEMS_PER_PAGE = 10
	const listRef = React.useRef<HTMLDivElement>(null)

	const totalPages = Math.ceil(treks.length / ITEMS_PER_PAGE)
	const startIndex = (activePage - 1) * ITEMS_PER_PAGE
	const visibleTreks = treks.slice(startIndex, startIndex + ITEMS_PER_PAGE)

	const handlePageChange = (newPage: number) => {
		setActivePage(newPage)
		listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	return (
		<Box px="md">
			<div ref={listRef}>
				<div>
					{visibleTreks.map((trek, index) => (
						<ExpeditionCard key={`${trek.id}-${index}`} trek={trek} />
					))}
				</div>
			</div>

			{totalPages > 1 && (
				<Center mt="xl" mb="xl">
					<Pagination
						value={activePage}
						onChange={handlePageChange}
						total={totalPages}
						size="md"
						radius="md"
						withEdges
						// Shared pagination skin (app/style.scss)
						classNames={{ root: 'gt-pagination' }}
					/>
				</Center>
			)}
		</Box>
	)
}
